<template>
  <b-card>
    <b-row>

      <!-- User Info: Left col -->
      <b-col
        cols="21"
        xl="6"
        class="d-flex justify-content-between flex-column"
      >
        <!-- User Avatar & Action Buttons -->
        <div class="d-flex justify-content-start">
          <b-avatar
            :src="cliente.avatar"
            :text="avatarText(cliente.nome.split(' ').slice(0, 2).join(' '))"
            :variant="`light-${resolveGrupoUsuarioVariante(cliente.usuarios[0].grupo.grupousuario)}`"
            size="104px"
            rounded
          />
          <div class="d-flex flex-column ml-1">
            <div class="mb-1">
              <h4 class="mb-0">
                {{ cliente.nome }}
              </h4>
              <span class="card-text">{{ cliente.usuarios[0].login }}</span>
            </div>
            <div class="d-flex flex-wrap">
              <b-button
                v-if="$can('update', 'ADMIN_B2C')"
                :to="{ name: 'app-admin-edit-cliente', params: { id: cliente.id } }"
                variant="primary"
              >
                Edit
              </b-button>
            </div>
          </div>
        </div>

        <!-- User Stats -->
        <div class="d-flex align-items-center mt-2">
          <div class="d-flex align-items-center mr-2">
            <b-avatar
              variant="light-primary"
              rounded
            >
              <feather-icon
                icon="DollarSignIcon"
                size="18"
              />
            </b-avatar>
            <div class="ml-1">
              <h5 class="mb-0">
                0
              </h5>
              <small>Vendas mensais</small>
            </div>
          </div>

          <div class="d-flex align-items-center">
            <b-avatar
              variant="light-success"
              rounded
            >
              <feather-icon
                icon="TrendingUpIcon"
                size="18"
              />
            </b-avatar>
            <div class="ml-1">
              <h5 class="mb-0">
                R$ 0
              </h5>
              <small>Lucro anual</small>
            </div>
          </div>
        </div>
      </b-col>

      <!-- Right Col: Table -->
      <b-col
        cols="12"
        xl="6"
      >
        <table class="mt-2 mt-xl-0 w-100">
          <tr>
            <th class="pb-50">
              <feather-icon
                icon="UserIcon"
                class="mr-75"
              />
              <span class="font-weight-bold">Login</span>
            </th>
            <td class="pb-50">
              {{ cliente.usuarios[0].login }}
            </td>
          </tr>
          <tr>
            <th class="pb-50">
              <feather-icon
                icon="CheckIcon"
                class="mr-75"
              />
              <span class="font-weight-bold">Status</span>
            </th>
            <td class="pb-50 text-capitalize">

              <b-badge
                pill
                :variant="`light-${resolveUserStatusVariant(cliente.ativo)}`"
                class="text-capitalize"
              >
                {{ cliente.ativo === 1 ? 'Ativo' : 'Inativo' }}
              </b-badge>
            </td>
          </tr>
          <tr>
            <th class="pb-50">
              <feather-icon
                :icon="resolveGrupoUsuarioIcon(cliente.usuarios[0].grupo.grupousuario)"
                :variant="`light-${resolveGrupoUsuarioVariante(cliente.usuarios[0].grupo.grupousuario)}`"
                class="mr-75"
              />
              <span class="font-weight-bold">Grupo</span>
            </th>
            <td class="pb-50 text-capitalize">
              {{ cliente.usuarios[0] !== null ? cliente.usuarios[0].grupo.grupousuario : '' }}
            </td>
          </tr>
          <tr>
            <th class="pb-50">
              <feather-icon
                icon="FlagIcon"
                class="mr-75"
              />
              <span class="font-weight-bold">ConfigThemeEcommecer</span>
            </th>
            <td class="pb-50">
              {{ cliente.cliente_enderecos[0].pais.pais_nome }}
            </td>
          </tr>

          <tr>
            <th class="pb-3">
              <feather-icon
                icon="MapPinIcon"
                class="mr-75"
              />
              <span class="font-weight-bold">Endereco</span>
            </th>
            <div class="pt-0">
              <h6 class="mb-25">
                {{ cliente.cliente_enderecos[0].cep | maskCep }}, {{ cliente.cliente_enderecos[0].endereco }},
                N°. {{ cliente.cliente_enderecos[0].numero }}
              </h6>
              <p class="card-text mb-25">
                {{ cliente.cliente_enderecos[0].complemento }}, {{ cliente.cliente_enderecos[0].bairro }},
                {{ cliente.cliente_enderecos[0].cidade.cidade_nome }}, {{ cliente.cliente_enderecos[0].cidade.uf_id }}
              </p>
            </div>
          </tr>
        </table>
      </b-col>

    </b-row>
  </b-card>
</template>

<script>
import {
  BCard, BButton, BAvatar, BRow, BCol, BBadge,
} from 'bootstrap-vue'
import { avatarText } from '@core/utils/filter'
import useClienteList from '@/views/apps/clientes/clientes-list/useClienteList'

export default {
  components: {
    BCard, BButton, BRow, BCol, BAvatar, BBadge,
  },
  filters: {
    maskCep: value => {
      if (!value) return ''
      value = value.toString().replace(/\D/g, '')
      value = value.padStart(8, '0')
      return `${value.substring(0, 5)}-${value.substring(5)}`
    },
  },
  props: {
    userData: {
      type: Object,
      required: true,
    },
  },
  computed: {
    cliente() {
      return {
        id: this.userData.id,
        nome: this.userData.nome,
        ativo: 1,
        usuarios: [
          {
            id: 1,
            login: this.userData.usuarios[0].login,
            grupo: this.userData.usuarios[0].grupo,
          },
        ],
        cliente_enderecos: this.userData.cliente_enderecos !== null ? [
          {
            id: this.userData.cliente_enderecos[0].id,
            cep: this.userData.cliente_enderecos[0].cep,
            endereco: this.userData.cliente_enderecos[0].endereco,
            numero: this.userData.cliente_enderecos[0].numero,
            complemento: this.userData.cliente_enderecos[0].complemento,
            bairro: this.userData.cliente_enderecos[0].bairro,
            cidade: this.userData.cliente_enderecos[0].cidade !== null ? {
              cidade_nome: this.userData.cliente_enderecos[0].cidade.cidade_nome,
              uf_id: this.userData.cliente_enderecos[0].cidade.uf_id,
            } : {
              cidade_nome: null,
              uf_id: null,
            },
            pais: this.userData.cliente_enderecos[0].pais !== null ? {
              pais_nome: this.userData.cliente_enderecos[0].pais.pais_nome,
            } : {
              pais_nome: null,
            },
          },
        ] : [],
      }
    },
  },
  setup() {
    const { resolveUserStatusVariant, resolveGrupoUsuarioVariante, resolveGrupoUsuarioIcon } = useClienteList()
    return {
      avatarText,
      resolveUserStatusVariant,
      resolveGrupoUsuarioVariante,
      resolveGrupoUsuarioIcon,
    }
  },
}
</script>

<style>

</style>
